import React, { useMemo } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { brandHeading } from '@/utils/brand-typography'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import SellOutlineIcon from '@assets/images/icons/v2/sell-outline.svg'
import { ButtonSquare } from '@/modules/base/components/buttons'
import { VARIANTS as BUTTON_VARIANTS } from '@/modules/base/components/buttons/square/button-square'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import { dateConstants } from '@/modules/base/util/constants'
import getSums from '@/modules/moduleSubscription/components/_helpers/getSums'
import getVat1Germany from '@/modules/moduleSubscription/components/_helpers/getVat1Germany'
import { ShoppingCartItems } from '@/modules/moduleSubscription/types/ShoppingCart'
import type { CouponDataType } from '@/modules/base/components/modal/upgrade-modal/order-overview/summary/overview-summary'
import getSelectionLines, { SelectionLine } from '../_helpers/getSelectionLines'
import formatPrice from '../_helpers/formatPrice'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    padding: 24,
    background: theme.palette.background.paper,
    borderRadius: theme.borderRadiusExtraLarge,
    border: `1px solid ${theme.palette.brand.divider}`,
    color: theme.palette.brand.text.primary,
    boxShadow: '0px 1px 12px 0px #15233F14',
    fontSize: theme.brandTypography.text.regular.fontSize,
  },
  title: {
    margin: 0,
    ...brandHeading(theme, 'h6'),
  },
  // "Tariflaufzeit" and "Abrechnungszeitraum" side by side
  terms: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: 32,
    rowGap: 12,
    margin: 0,
  },
  termLabel: {
    fontWeight: theme.brandTypography.weights.medium,
  },
  termValue: {
    margin: 0,
    color: theme.palette.brand.text.secondary,
  },
  lines: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 16px',
    borderRadius: theme.borderRadiusLarge,
    background: theme.backgroundColor,
  },
  line: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '14px 0',
    '& + $line': {
      borderTop: `1px solid ${theme.palette.brand.divider}`,
    },
  },
  lineHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  lineTitle: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    fontWeight: theme.brandTypography.weights.medium,
  },
  lineTitlePlan: {
    fontWeight: theme.brandTypography.weights.semibold,
  },
  // only as wide as its text - the line is a flex column and would stretch it
  discount: {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    padding: '4px 8px',
    borderRadius: theme.borderRadius,
    background: theme.palette.brand.promotion,
    fontSize: theme.brandTypography.text.tiny.fontSize,
  },
  linePrices: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    whiteSpace: 'nowrap',
  },
  lineTotal: {
    fontWeight: theme.brandTypography.weights.medium,
  },
  regularTotal: {
    color: theme.palette.brand.text.secondary,
    fontSize: theme.brandTypography.text.small.fontSize,
    textDecoration: 'line-through',
  },
  lineRow: {
    color: theme.palette.brand.text.secondary,
    fontSize: theme.brandTypography.text.small.fontSize,
  },
  empty: {
    color: theme.palette.brand.text.secondary,
  },
  sums: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    paddingTop: 20,
    borderTop: `1px solid ${theme.palette.brand.divider}`,
    color: theme.palette.brand.text.secondary,
  },
  couponRow: {
    alignItems: 'center',
    color: theme.palette.brand.text.primary,
  },
  couponLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
  },
  couponRemove: {
    textDecoration: 'underline',
  },
  sumRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    // an amount never breaks between the number and "€"
    '& > span:last-child': {
      whiteSpace: 'nowrap',
    },
  },
  dueToday: {
    alignItems: 'baseline',
    color: theme.palette.brand.text.heading,
    fontWeight: theme.brandTypography.weights.semibold,
    fontSize: theme.brandTypography.text.medium.fontSize,
  },
  dueTodayAmount: {
    color: theme.palette.brand.main,
    ...brandHeading(theme, 'h5'),
  },
  // the "*" behind the prices of the plans and add-ons
  priceHint: {
    margin: 0,
    color: theme.palette.brand.text.secondary,
    fontSize: theme.brandTypography.text.tiny.fontSize,
  },
}))

interface SelectionSidebarProps {
  items: ShoppingCartItems
  period: string
  couponCode?: CouponDataType | null
  // the coupon input in "Daten & Zahlung" - replaced by the discount row once a coupon is applied
  couponSlot?: React.ReactNode
  onCouponRemove?: () => void
  // the step's primary action, e.g. "Weiter zu Daten & Zahlung"
  action?: React.ReactNode
  // shown below the action, e.g. the AGB notice
  footer?: React.ReactNode
  // mobile: the full screen drawer shows the title in its own header
  showTitle?: boolean
  className?: string
}

const SelectionSidebar = (props: SelectionSidebarProps) => {
  const { items, period, couponCode, couponSlot, onCouponRemove, action, footer, showTitle = true, className } = props
  const classes = useStyles()

  const vat1Germany = useMemo(() => getVat1Germany(), [])
  const lines = useMemo(() => getSelectionLines(items, period), [items, period])
  const sums = useMemo(() => getSums(items, vat1Germany, couponCode, period), [items, vat1Germany, couponCode, period])
  const texts = lang.upgrade.wizard.sidebar

  const renderLine = (line: SelectionLine) => (
    <div key={line.id} className={classes.line} data-qs-id={`checkout-selection-line-${line.name}`}>
      <div className={classes.lineHeader}>
        <div className={clsx(classes.lineTitle, line.isPlan && classes.lineTitlePlan)}>
          {texts.lineItemTitle(line.isPlan ? 1 : line.count, line.title)}
        </div>
        <div className={classes.linePrices}>
          {line.regularTotal && <span className={classes.regularTotal}>{formatCurrency(line.regularTotal)}</span>}
          <span className={classes.lineTotal}>{formatCurrency(line.total)}</span>
        </div>
      </div>
      {line.rows.map(row => (
        <div key={row.id} className={classes.lineRow} data-qs-id={`checkout-selection-row-${line.name}-${row.id}`}>
          {formatPrice(row.pricePerMonth)}
          {line.count > 1 && ` × ${line.count}`}
          {` × ${texts.months(row.months)}`}
        </div>
      ))}
      {line.discountPercent && (
        <div className={classes.discount}>
          <SVGIcon svg={SellOutlineIcon} width={16} height={16} fill='currentColor' />
          {texts.discount(line.discountPercent, line.discountMonths)}
        </div>
      )}
    </div>
  )

  // the minimum term is 12 months for both payment intervals (see the legal hint), as in the former shopping cart
  const cartTexts = lang.moduleSubscription.list.shoppingCart
  const terms = lines.length > 0 && (
    <dl className={classes.terms} data-qs-id='checkout-selection-terms'>
      <div>
        <dt className={classes.termLabel}>{cartTexts.periodTitleLabel}</dt>
        <dd className={classes.termValue}>{cartTexts.periodInMonths(12)}</dd>
      </div>
      <div>
        <dt className={classes.termLabel}>{cartTexts.billingPeriodLabel}</dt>
        <dd className={classes.termValue} data-qs-id='checkout-selection-billing-period'>{cartTexts.periods[period]}</dd>
      </div>
    </dl>
  )

  const details = (
    <>
      {terms}
      {lines.length
        ? <div className={classes.lines}>{lines.map(renderLine)}</div>
        : <div className={classes.empty}>{texts.empty}</div>}
      {lines.length > 0 && (
        <div className={classes.sums}>
          {!couponCode && couponSlot}
          <div className={classes.sumRow}>
            <span>{texts.subtotal(period)}</span>
            <span>{formatCurrency(sums.totalNet)}</span>
          </div>
          {couponCode && (
            <div className={clsx(classes.sumRow, classes.couponRow)} data-qs-id='checkout-selection-coupon-discount'>
              <div className={classes.couponLabel}>
                <span>{texts.couponDiscount(couponCode.code)}</span>
                {onCouponRemove && (
                  <ButtonSquare
                    variant={BUTTON_VARIANTS.link}
                    className={classes.couponRemove}
                    label={texts.couponRemove}
                    dataQsId='checkout-coupon-remove'
                    onClick={onCouponRemove}
                  />
                )}
              </div>
              <span>{formatCurrency(-sums.totalDiscount)}</span>
            </div>
          )}
          <div className={classes.sumRow}>
            <span>{texts.vat(sums.vatPercent)}</span>
            <span>{formatCurrency(sums.totalVat)}</span>
          </div>
        </div>
      )}
    </>
  )

  const dueToday = lines.length > 0 && (
    <div className={clsx(classes.sumRow, classes.dueToday)}>
      <span>{texts.dueToday}</span>
      <span className={classes.dueTodayAmount} data-typography='heading' data-qs-id='checkout-selection-due-today'>{formatCurrency(sums.totalGross)}</span>
    </div>
  )

  return (
    <aside className={clsx(classes.root, className)} data-qs-id='checkout-selection-sidebar'>
      {showTitle && <h3 className={classes.title}>{texts.title}</h3>}
      {details}
      {dueToday}
      {lines.length > 0 && (
        <p className={classes.priceHint} data-qs-id='checkout-selection-price-hint'>
          {period === dateConstants.YEAR ? cartTexts.legalMessage : cartTexts.legalMessageMonth}
        </p>
      )}
      {action}
      {footer}
    </aside>
  )
}

export default SelectionSidebar
